import { Link } from 'react-router-dom'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import {
  AskAiAvailability,
  BillingProduct,
} from '@tribeplatform/gql-client/types'
import { T } from '@tribeplatform/react-components/i18n'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'

interface Props {
  addon: BillingProduct
  askAiAvailability: AskAiAvailability
  addonIsInstalled: boolean
  canInstallAddon: boolean
  isUpdating: boolean
  onTrialSettingChange: (value: boolean) => Promise<void>
  onInstallAddon: () => Promise<void>
}

export const AskAISettingsContent = ({
  addon,
  askAiAvailability,
  addonIsInstalled,
  isUpdating,
  canInstallAddon,
  onTrialSettingChange,
  onInstallAddon,
}: Props) => {
  return (
    <LoadingOverlay isLoading={isUpdating}>
      <div className="flex flex-col gap-y-6">
        <Card className="bg-surface-subdued" transparent>
          <Card.Content>
            <div className="flex items-center gap-x-3 justify-between">
              <Avatar src={addon?.imageUrls?.at(0)} size="4x" rounded="base" />
              <div className="flex flex-col gap-y-2 flex-grow">
                <h3 className="text-heading-xs font-medium text-content">
                  {addon?.name}
                </h3>
                <p className="text-content-subdued text-sm">
                  {addon?.description}
                </p>
              </div>
              <div className="flex items-center gap-x-2">
                {!addonIsInstalled ? (
                  <>
                    {canInstallAddon ? (
                      <Button
                        variant="primary"
                        onClick={onInstallAddon}
                        loading={isUpdating}
                      >
                        <T defaultMessage="Install" id="Generics.Install" />
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        as={Link}
                        to={adminRoutes.appStore.addons.detail(addon?.id)}
                      >
                        <T defaultMessage="Upgrade" id="Generics.Upgrade" />
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    variant="primary"
                    as={Link}
                    to={adminRoutes.appStore.addons.detail(addon?.id)}
                  >
                    <T defaultMessage="Settings" id="Generics.Settings" />
                  </Button>
                )}
              </div>
            </div>
          </Card.Content>
        </Card>

        {!addonIsInstalled && (
          <FormControl.Toggle
            name="ask-ai-preview"
            checked={askAiAvailability !== AskAiAvailability.disabled}
            label={
              <T defaultMessage="Try for Free" id="Admin.AskAISettings.Try" />
            }
            helperText={
              <T
                defaultMessage="Try Ask AI without installing the add-on. The feature will only be available to you and other staff."
                id="Admin.AskAISettings.Try.Description"
              />
            }
            disabled={isUpdating}
            onChange={value => {
              onTrialSettingChange(value)
            }}
          />
        )}
      </div>
    </LoadingOverlay>
  )
}
